<template>
  <div class="materials-admin-page">
    <!-- User Filter -->
    <div class="user-filter-container">
      <div class="user-filter">
        <label class="filter-label">Filter by User:</label>
        <b-form-select
          v-model="selectedUserId"
          :options="userOptions"
          class="user-select"
          @change="onUserFilterChange"
        />
      </div>
    </div>

    <!-- Page Header -->
    <div class="page-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h3 class="mb-1">Materials Quotes Administration</h3>
          <p class="text-muted mb-0">
            {{ selectedUserId === 'all' ? 'Aggregate statistics for all users' : `Statistics for ${getSelectedUserName()}` }}
          </p>
        </div>
      </div>
    </div>

    <!-- Materials Quote Statistics -->
    <MaterialsQuoteStatistics 
      v-if="!loadingStats && quoteStats"
      :stats="quoteStats"
      :key="selectedUserId"
    />
    
    <!-- Loading State -->
    <div v-else-if="loadingStats" class="loading-container">
      <div class="d-flex justify-content-center align-items-center py-5">
        <b-spinner variant="primary" class="me-3"></b-spinner>
        <span class="text-muted">Loading statistics...</span>
      </div>
    </div>
    
    <!-- Error State -->
    <div v-else class="error-container">
      <div class="alert alert-warning text-center">
        <i class="fas fa-exclamation-triangle me-2"></i>
        Unable to load statistics. Please try refreshing the page.
      </div>
    </div>

    <!-- Materials Quotes Table Section -->
    <div class="quotes-table-section mt-5">
      <div class="section-header mb-4">
        <h5 class="section-title">Recent Materials Quotes</h5>
      </div>
      
      <!-- Filters -->
      <div class="filters-section mb-4">
        <div class="card">
          <div class="card-body">
            <div class="row">
              <div class="col-md-3">
                <div class="form-group">
                  <label for="search">Search</label>
                  <input
                    id="search"
                    v-model="filters.search"
                    type="text"
                    class="form-control"
                    placeholder="Search quotes..."
                    @input="debouncedSearch"
                  />
                </div>
              </div>
              <div class="col-md-2">
                <div class="form-group">
                  <label for="status">Status</label>
                  <select id="status" v-model="filters.status" class="form-control" @change="loadQuotes">
                    <option value="">All Statuses</option>
                    <option value="DRAFT">Draft</option>
                    <option value="OPEN">Open</option>
                    <option value="SOLD">Sold</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="EXPIRED">Expired</option>
                  </select>
                </div>
              </div>
              <div class="col-md-2">
                <div class="form-group">
                  <label for="quoteStatus">Follow-up Status</label>
                  <select id="quoteStatus" v-model="filters.quoteStatus" class="form-control" @change="loadQuotes">
                    <option value="">All Follow-ups</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="NO_FOLLOW_UP">No Follow-up</option>
                    <option value="CONVERTED">Converted</option>
                    <option value="LOST">Lost</option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>&nbsp;</label>
                  <div>
                    <button class="btn btn-outline-secondary" @click="clearFilters" v-if="hasFilters">
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quotes Table -->
      <div class="card">
        <div class="card-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading materials quotes...</p>
          </div>

          <!-- No Results -->
          <div v-else-if="quotes.length === 0" class="text-center py-5">
            <i class="fas fa-box fa-3x text-muted mb-3"></i>
            <h5 class="text-muted">No materials quotes found</h5>
            <p class="text-muted">
              {{ hasFilters ? 'Try adjusting your filters' : 'No quotes match the current criteria' }}
            </p>
          </div>

          <!-- Quotes Table -->
          <div v-else>
            <b-table
              :items="quotes"
              :fields="tableFields"
              striped
              hover
              responsive
              @row-clicked="viewQuote"
              tbody-tr-class="clickable-row"
            >
              <template #cell(shortId)="data">
                <strong class="text-primary">{{ data.item.shortId }}</strong>
              </template>

              <template #cell(title)="data">
                <div>
                  <strong>{{ data.item.title }}</strong>
                  <div v-if="data.item.description" class="text-muted small">
                    {{ data.item.description.substring(0, 50) }}{{ data.item.description.length > 50 ? '...' : '' }}
                  </div>
                </div>
              </template>

              <template #cell(amount)="data">
                <div class="text-right">
                  <strong v-if="data.item.amount" class="text-success">${{ formatCurrency(data.item.amount) }}</strong>
                  <span v-else class="text-muted">-</span>
                </div>
              </template>

              <template #cell(expirationDate)="data">
                <div v-if="data.item.expirationDate">
                  {{ formatDate(data.item.expirationDate) }}
                  <div v-if="isExpired(data.item.expirationDate)" class="text-danger small">
                    <i class="fas fa-exclamation-triangle"></i> Expired
                  </div>
                  <div v-else-if="isExpiringSoon(data.item.expirationDate)" class="text-warning small">
                    <i class="fas fa-clock"></i> Expires Soon
                  </div>
                </div>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(customer)="data">
                <div v-if="data.item.customer && data.item.customer.name">
                  <strong>{{ data.item.customer.name }}</strong>
                  <div v-if="data.item.customer.company" class="text-muted small">
                    {{ data.item.customer.company }}
                  </div>
                  <div v-if="data.item.customer.email" class="text-muted small">
                    {{ data.item.customer.email }}
                  </div>
                </div>
                <span v-else class="text-muted">No customer</span>
              </template>

              <template #cell(status)="data">
                <b-badge :variant="getStatusVariant(data.item.status)">
                  {{ data.item.status }}
                </b-badge>
              </template>

              <template #cell(quoteStatus)="data">
                <b-badge :variant="getQuoteStatusVariant(data.item.quoteStatus)">
                  {{ getQuoteStatusText(data.item.quoteStatus) }}
                </b-badge>
              </template>

              <template #cell(createdAt)="data">
                <div>
                  {{ formatDate(data.item.createdAt) }}
                  <div class="text-muted small">
                    by {{ getUserDisplayName(data.item.createdBy) }}
                  </div>
                </div>
              </template>
            </b-table>

            <!-- Pagination -->
            <div v-if="pagination.totalPages > 1" class="pagination-section">
              <b-pagination
                v-model="pagination.currentPage"
                :total-rows="pagination.totalCount"
                :per-page="pagination.limit"
                :limit="5"
                align="center"
                @change="onPageChange"
                class="mt-3"
              />
              <div class="pagination-info text-center">
                Showing {{ ((pagination.currentPage - 1) * pagination.limit) + 1 }} to 
                {{ Math.min(pagination.currentPage * pagination.limit, pagination.totalCount) }} of 
                {{ pagination.totalCount }} quotes
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import MaterialsQuoteStatistics from '@/components/MaterialsQuoteStatistics.vue'
import { debounce } from 'lodash'
import moment from 'moment'

export default {
  name: 'MaterialsAdminPage',
  components: {
    MaterialsQuoteStatistics
  },
  data() {
    return {
      quoteStats: null,
      loadingStats: true,
      selectedUserId: 'all',
      users: [],
      loadingUsers: false,
      loading: false,
      quotes: [],
      filters: {
        search: '',
        status: '',
        quoteStatus: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 20,
        hasNext: false,
        hasPrev: false
      },
      tableFields: [
        { key: 'shortId', label: 'Quote #', sortable: true },
        { key: 'title', label: 'Title', sortable: true },
        { key: 'amount', label: 'Amount', sortable: true, class: 'text-right' },
        { key: 'expirationDate', label: 'Expires', sortable: true },
        { key: 'customer', label: 'Customer', sortable: false },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'quoteStatus', label: 'Follow-up', sortable: true },
        { key: 'createdAt', label: 'Created', sortable: true }
      ]
    }
  },
  computed: {
    userOptions() {
      const options = [
        { value: 'all', text: 'All Users' }
      ]
      
      if (this.users && this.users.length > 0) {
        this.users.forEach(user => {
          options.push({
            value: user.email,
            text: `${user.firstName} ${user.lastName} (${user.type})`
          })
        })
      }
      
      return options
    },
    selectedUserEmail() {
      return this.selectedUserId === 'all' ? null : this.selectedUserId
    },
    hasFilters() {
      return this.filters.search || this.filters.status || this.filters.quoteStatus
    }
  },
  async mounted() {
    await Promise.all([
      this.loadUsers(),
      this.loadQuoteStats(),
      this.loadQuotes()
    ])
  },
  methods: {
    async loadUsers() {
      this.loadingUsers = true
      try {
        const response = await axios.get('/services/v2/users/list')
        this.users = response.data.users
      } catch (error) {
        console.error('Error loading users:', error)
      } finally {
        this.loadingUsers = false
      }
    },

    async loadQuoteStats() {
      this.loadingStats = true
      try {
        const params = { adminMode: 'true' }
        if (this.selectedUserEmail) {
          params.userEmail = this.selectedUserEmail
        }
        
        const response = await axios.get('/services/v2/materials-quotes/stats', { params })
        this.quoteStats = response.data
      } catch (error) {
        console.error('Error loading materials quote stats:', error)
        this.$bvToast.toast('Error loading materials quote statistics', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.loadingStats = false
      }
    },

    async loadQuotes() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          limit: this.pagination.limit,
          sortBy: this.filters.sortBy,
          sortOrder: this.filters.sortOrder
        }

        if (this.filters.search) params.search = this.filters.search
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.quoteStatus) params.quoteStatus = this.filters.quoteStatus
        if (this.selectedUserEmail) params.createdBy = this.selectedUserEmail

        const response = await axios.get('/services/v2/materials-quotes', { params })
        this.quotes = response.data.quotes
        this.pagination = response.data.pagination
      } catch (error) {
        console.error('Error loading materials quotes:', error)
        this.$bvToast.toast('Error loading materials quotes', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    onUserFilterChange() {
      // Reload stats and quotes when user filter changes
      this.loadQuoteStats()
      this.loadQuotes()
    },

    getSelectedUserName() {
      if (this.selectedUserId === 'all') return 'All Users'
      const user = this.users.find(u => u.email === this.selectedUserId)
      return user ? `${user.firstName} ${user.lastName}` : 'Unknown User'
    },

    debouncedSearch: debounce(function() {
      this.pagination.currentPage = 1
      this.loadQuotes()
    }, 500),

    clearFilters() {
      this.filters = {
        search: '',
        status: '',
        quoteStatus: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      }
      this.pagination.currentPage = 1
      this.loadQuotes()
    },

    onPageChange(page) {
      this.pagination.currentPage = page
      this.loadQuotes()
    },

    viewQuote(quote) {
      // Navigate to the regular materials quotes page with the quote selected
      this.$router.push({
        name: 'MaterialsQuotesPage',
        params: { viewQuote: quote.shortId }
      })
    },

    getStatusVariant(status) {
      const variants = {
        'DRAFT': 'secondary',
        'OPEN': 'info',
        'SOLD': 'success',
        'REJECTED': 'danger',
        'EXPIRED': 'warning'
      }
      return variants[status] || 'secondary'
    },

    getQuoteStatusVariant(status) {
      const variants = {
        'IN_PROGRESS': 'primary',
        'NO_FOLLOW_UP': 'secondary',
        'CONVERTED': 'success',
        'LOST': 'danger'
      }
      return variants[status] || 'secondary'
    },

    getQuoteStatusText(status) {
      const texts = {
        'IN_PROGRESS': 'In Progress',
        'NO_FOLLOW_UP': 'No Follow-up',
        'CONVERTED': 'Converted',
        'LOST': 'Lost'
      }
      return texts[status] || status
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString()
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    isExpired(expirationDate) {
      if (!expirationDate) return false
      return moment(expirationDate).isBefore(moment(), 'day')
    },

    isExpiringSoon(expirationDate) {
      if (!expirationDate) return false
      const expDate = moment(expirationDate)
      const today = moment()
      const daysUntilExpiry = expDate.diff(today, 'days')
      return daysUntilExpiry >= 0 && daysUntilExpiry <= 7
    },

    getUserDisplayName(email) {
      if (!email) return 'Unknown'
      const atIndex = email.indexOf('@')
      if (atIndex === -1) return email
      
      const namePart = email.substring(0, atIndex)
      return namePart.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join(' ')
    }
  }
}
</script>

<style lang="scss" scoped>
.materials-admin-page {
  .user-filter-container {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1.5rem;
  }

  .user-filter {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .filter-label {
    margin: 0;
    font-weight: 500;
    color: #6c757d;
    white-space: nowrap;
  }

  .user-select {
    min-width: 200px;
    border: 1px solid #ced4da;
    border-radius: 6px;
    
    &:focus {
      border-color: #007bff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }

  .page-header {
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 1rem;
  }

  .section-title {
    color: #495057;
    font-weight: 600;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e9ecef;
  }

  .clickable-row {
    cursor: pointer;
    
    &:hover {
      background-color: #f8f9fa;
    }
  }

  .pagination-info {
    color: #6c757d;
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }

  .loading-container, .error-container {
    margin: 2rem 0;
  }

  .alert {
    border-radius: 8px;
    border: none;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
}
</style>
